import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { useState } from "react";

import { IDraftMessage } from "../../../store/queries/contractModelDrafts";
import { StepId, WIZARD_STEPS } from "./steps";

const stepTitle = (stepId: string | null): string | null =>
  WIZARD_STEPS.find((s) => s.id === stepId)?.title ?? null;

interface IProps {
  stepId: StepId;
  messages: IDraftMessage[];
  busy: boolean;
  regenerating: boolean;
  onAdd: (body: string, stepId: StepId) => Promise<void>;
  onDelete: (messageId: string) => void;
  onRegenerate: () => void;
}

/**
 * Where the reviewer tells the agent what to do differently.
 *
 * Two deliberate design choices, both of which look like omissions until you know why:
 *
 * 1. **This is not a chat.** No speech bubbles — a numbered list of instructions. The agent CANNOT
 *    reply in real time (generation is a multi-minute batch job, and a conversational loop is blocked
 *    on a pending API decision). A chat UI would promise a reply that never comes.
 *
 * 2. **Writing an instruction does not start a generation.** They queue. She usually has several things
 *    to say before she wants the model re-derived, and firing a minutes-long job per message would be
 *    both expensive and maddening.
 *
 * The copy below spells out the whole mechanism, because the first version did not and it was not
 * guessable: that instructions accumulate ACROSS steps, that nothing happens until Regenerate, that
 * Regenerate re-derives the WHOLE model, and that confirmations survive it.
 */
function InstructionPanel({
  stepId,
  messages,
  busy,
  regenerating,
  onAdd,
  onDelete,
  onRegenerate,
}: IProps) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const pending = messages.filter((m) => !m.appliedInVersionId);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onAdd(text, stepId);
      setBody("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card border-round p-3 mt-4 border-1 surface-border">
      <div className="font-semibold mb-1">
        Ask the agent to change something
      </div>

      <p className="mt-0 mb-3 text-sm text-700 line-height-3">
        Write what the agent got wrong, in plain English. Instructions{" "}
        <strong>queue up</strong> — nothing changes until you press{" "}
        <strong>Regenerate</strong>. When you do, the agent re-reads every
        document and rebuilds the whole model, applying{" "}
        <strong>every instruction you have written, on any step</strong> — not
        just this one. Recommendations that don&rsquo;t change keep the verdict
        you already gave them, so you never review the same thing twice.
      </p>
      <p className="mt-0 mb-3 text-sm text-600 line-height-3">
        If you already know the right answer, it&rsquo;s quicker to just edit
        the fields on a recommendation and press Confirm — that takes effect
        immediately, with no rebuild.
      </p>

      {messages.length > 0 && (
        <ol className="mt-2 mb-3 pl-3 text-sm">
          {messages.map((m) => (
            <li key={m.id} className="mb-2">
              <span
                className={
                  m.appliedInVersionId ? "text-color-secondary" : "text-900"
                }
              >
                {m.body}
              </span>
              <div className="flex align-items-center gap-2 mt-1">
                {m.stepId && m.stepId !== stepId ? (
                  <small className="text-color-secondary">
                    written on {stepTitle(m.stepId) ?? m.stepId}
                  </small>
                ) : null}
                {m.recommendationId ? (
                  <small className="text-color-secondary">
                    about one recommendation
                  </small>
                ) : null}
                {m.appliedInVersionId ? (
                  <small className="text-color-secondary">
                    <i
                      className="pi pi-check mr-1"
                      style={{ fontSize: "0.7rem" }}
                    />
                    applied
                  </small>
                ) : (
                  <>
                    <small className="text-orange-700 font-medium">
                      Not applied yet
                    </small>
                    <Button
                      label="Remove"
                      className="p-button-text p-button-sm p-0"
                      disabled={busy}
                      onClick={() => onDelete(m.id)}
                    />
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      <InputTextarea
        className="w-full"
        rows={2}
        autoResize
        placeholder="e.g. Treat the office column as a separate entity. The psych codes follow the 2024 grid, not the amendment."
        value={body}
        disabled={busy || saving || regenerating}
        onChange={(e) => setBody(e.target.value)}
      />

      <div className="flex justify-content-between align-items-center mt-2 gap-3">
        {/* Honest about scope. We do NOT claim the re-derive is limited to this step — it is global.
            Narrowness is an outcome, not a promise, and pretending otherwise is how you ambush someone
            when a change lands on a step she already signed off. */}
        <small className="text-600">
          Applies to the whole model, not just this step — the agent re-reads
          everything.
        </small>
        <div className="flex gap-2 white-space-nowrap">
          <Button
            label="Add instruction"
            icon="pi pi-plus"
            className="p-button-sm p-button-outlined"
            disabled={
              busy || saving || regenerating || body.trim().length === 0
            }
            onClick={submit}
          />
          {/* The rebuild is reachable from where she is thinking, not only from a bar at the top of the
              page she has already scrolled past. */}
          <Button
            label={
              regenerating
                ? "Regenerating…"
                : `Regenerate${
                    pending.length > 0 ? ` (${pending.length})` : ""
                  }`
            }
            icon="pi pi-refresh"
            className="p-button-sm"
            disabled={busy || regenerating || pending.length === 0}
            onClick={onRegenerate}
          />
        </div>
      </div>
    </div>
  );
}

export default InstructionPanel;
